import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useToast } from '@chakra-ui/react';
import { FiMail, FiLock, FiArrowRight, FiEye, FiEyeOff, FiLogIn, FiHome } from 'react-icons/fi';

import {
  RegisterPage, GameBg, PageBody,
  CentreCol, RegisterCard, FieldGroup,
  SubmitBtn, CardFooter, ErrorText, ShowHideBtn,
} from './RegisterStyle';

import { login } from '../../../store/auth/authSlice';
import { backendUrl } from "../../../utilities/axios";
const DISCORD_AUTH_URL = `${backendUrl}/api/auth/discord`;
const GOOGLE_AUTH_URL = `${backendUrl}/api/auth/google`;

const Login = () => {
  const [searchParams] = useSearchParams();
  const toast = useToast({ position: 'top', duration: 3000 });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);

  const { register, handleSubmit, formState: { errors } } = useForm();
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const [notActivated, setNotActivated] = useState(false);
  const [notActivatedEmail, setNotActivatedEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('auto_sent'); // auto_sent | sending | sent | error
  const [resendCooldown, setResendCooldown] = useState(60);
  const cooldownRef = useRef(null);

  useEffect(() => {
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  const startCooldown = (seconds = 60) => {
    setResendCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // Show error if Discord OAuth failed
  if (searchParams.get('error') === 'discord_failed') {
    toast({ description: 'فشل تسجيل الدخول عبر Discord، حاول مرة أخرى', status: 'error' });
  }

  // Show error if Google OAuth failed
  if (searchParams.get('error') === 'google_failed') {
    toast({ description: 'فشل تسجيل الدخول عبر Google، حاول مرة أخرى', status: 'error' });
  }

  const onSubmit = values => {
    setNotActivated(false);
    dispatch(login(values))
      .unwrap()
      .then(() => navigate('/'))
      .catch(error => {
        if (error.needsVerification) {
          setNotActivated(true);
          setNotActivatedEmail(values.email);
          setResendStatus('auto_sent');
          startCooldown(60);
        } else {
          toast({ description: error.errors?.[0]?.msg, status: 'error' });
        }
      });
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      await axios.post('/api/resend_verification', { email: notActivatedEmail });
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
    startCooldown(60);
  };

  return (
    <RegisterPage>
      <GameBg>
        <PageBody>
          <CentreCol>
            <RegisterCard>
              <div className="card-top-icon">
                <FiLogIn size={18} />
              </div>

              <h2 className="card-title">أهلاً بعودتك</h2>
              <p className="card-subtitle">سجّل دخولك للوصول إلى لوحة التحكم الخاصة بك</p>

              <form onSubmit={handleSubmit(onSubmit)}>
                <FieldGroup>
                  <span className="field-label">البريد الإلكتروني</span>
                  <div className="field-wrap">
                    <FiMail className="field-icon" />
                    <input
                      type="email"
                      placeholder="you@email.com"
                      {...register('email', { required: 'هذا الحقل مطلوب' })}
                    />
                  </div>
                  {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
                </FieldGroup>

                <FieldGroup>
                  <span className="field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Link to="/reset_password" style={{ color: '#c8a040', fontSize: '10px', fontWeight: 600, textDecoration: 'none' }}>
                      نسيت كلمة المرور؟
                    </Link>
                    كلمة المرور
                  </span>
                  <div className="field-wrap">
                    <FiLock className="field-icon" />
                    <input
                      type={isPasswordShown ? 'text' : 'password'}
                      placeholder="••••••••"
                      style={{ paddingRight: '36px' }}
                      {...register('password', {
                        required: 'هذا الحقل مطلوب',
                        minLength: { value: 8, message: 'يجب أن تكون كلمة المرور 8 أحرف على الأقل' },
                      })}
                    />
                    <ShowHideBtn type="button" onClick={() => setIsPasswordShown(v => !v)}>
                      {isPasswordShown ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                    </ShowHideBtn>
                  </div>
                  {errors.password && <ErrorText>{errors.password.message}</ErrorText>}
                </FieldGroup>

                {notActivated && (
                  <div style={{
                    background: 'rgba(200,160,64,0.08)', border: '1px solid rgba(200,160,64,0.3)',
                    borderRadius: 10, padding: '12px 14px', marginBottom: 12,
                  }}>
                    <p style={{ color: '#c8a040', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                      ✉️ حسابك غير مفعّل
                    </p>
                    <p style={{ color: '#aaa', fontSize: 12, marginBottom: 10 }}>
                      {resendStatus === 'auto_sent' || resendStatus === 'sent'
                        ? `تم إرسال رابط التفعيل إلى ${notActivatedEmail}`
                        : resendStatus === 'error'
                          ? 'تعذر الإرسال، حاول مرة أخرى'
                          : 'جارٍ إرسال رابط التفعيل…'
                      }
                    </p>
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resendCooldown > 0 || resendStatus === 'sending'}
                      style={{
                        width: '100%', padding: '9px',
                        background: resendCooldown > 0 ? '#2a2a2a' : 'linear-gradient(90deg,#c0392b,#e74c3c)',
                        color: resendCooldown > 0 ? '#555' : '#fff',
                        border: 'none', borderRadius: 7, fontWeight: 700,
                        fontSize: 13, cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
                        transition: 'all .2s',
                      }}
                    >
                      {resendStatus === 'sending'
                        ? 'جارٍ الإرسال…'
                        : resendCooldown > 0
                          ? `إعادة الإرسال (${resendCooldown}ث)`
                          : 'إعادة إرسال رابط التفعيل'
                      }
                    </button>
                  </div>
                )}

                <SubmitBtn type="submit" disabled={auth.isLoading}>
                  {auth.isLoading
                    ? 'جارٍ تسجيل الدخول…'
                    : <>تسجيل الدخول <FiArrowRight size={16} /></>
                  }
                </SubmitBtn>
              </form>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 12px' }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
                <span style={{ color: '#555', fontSize: 11 }}>أو</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
              </div>

              {/* Discord Button */}
              <a
                href={DISCORD_AUTH_URL}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '12px',
                  background: '#5865F2',
                  borderRadius: 8,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 14,
                  textDecoration: 'none',
                  marginBottom: 14,
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <DiscordIcon />
                تسجيل الدخول عبر Discord
              </a>

              {/* Google Button */}
              <a
                href={GOOGLE_AUTH_URL}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '12px',
                  background: '#fff',
                  borderRadius: 8,
                  color: '#1f1f1f',
                  fontWeight: 700,
                  fontSize: 14,
                  textDecoration: 'none',
                  marginBottom: 14,
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <GoogleIcon />
                تسجيل الدخول عبر Google
              </a>

              <CardFooter>
                <span className="footer-text">ليس لديك حساب؟</span>
                <Link to="/register" className="signin-link">
                  إنشاء حساب جديد <FiArrowRight size={12} />
                </Link>
              </CardFooter>

              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Link to="/" style={{ color: '#555', fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <FiHome size={12} /> العودة إلى الصفحة الرئيسية
                </Link>
              </div>
            </RegisterCard>
          </CentreCol>
        </PageBody>
      </GameBg>
    </RegisterPage>
  );
};

const DiscordIcon = () => (
  <svg width="20" height="20" viewBox="0 0 71 55" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z" fill="white" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#FFC107" d="M43.6 20.5H42V20.5H24V27.5H35.3C33.7 32.1 29.3 35.5 24 35.5C17.1 35.5 11.5 29.9 11.5 23C11.5 16.1 17.1 10.5 24 10.5C27.1 10.5 29.9 11.6 32.1 13.5L37.4 8.2C33.9 5 29.2 3 24 3C12.4 3 3 12.4 3 24C3 35.6 12.4 45 24 45C35.6 45 45 35.6 45 24C45 22.8 44.9 21.6 44.6 20.5H43.6Z" />
    <path fill="#FF3D00" d="M6.3 14.7L12.9 19.5C14.6 15.3 18.9 12.5 24 12.5C27.1 12.5 29.9 13.6 32.1 15.5L37.4 10.2C33.9 7 29.2 5 24 5C16.5 5 10.1 8.9 6.3 14.7Z" />
    <path fill="#4CAF50" d="M24 45C29.1 45 33.8 43.1 37.3 40L31.4 35C29.4 36.4 26.9 37.3 24 37.3C18.8 37.3 14.3 33.8 12.7 29L6.1 33.8C9.8 40.1 16.4 45 24 45Z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20.5H24V27.5H35.3C34.6 29.9 33.1 32 31.4 33.5L31.4 33.5L37.3 38.5C42.1 34.1 45 27.5 45 20.5C45 22.8 44.9 21.6 44.6 20.5H43.6Z" />
  </svg>
);

export default Login;
